import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Loader2,
  Globe,
  Store,
  MapPin,
  Sparkles,
  Camera,
  UploadCloud,
  Save,
  MessageCircle,
  Copy,
  Bell,
  ExternalLink,
  Palette,
  Check,
  AtSign,
} from 'lucide-react';
import { getUserProfile, updateUserProfile, checkPublicUsernameAvailability, parseUsernameCheckPayload, API_BASE_URL } from '../api';
import {
  VENDOR_THEME_OPTIONS,
  normalizePublicVendorThemeId,
  getVendorThemeCssVars,
} from '../utils/publicVendorThemes';
import { getPublicSiteOrigin } from '../utils/publicSiteOrigin';

const inputClass =
  'w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 transition-shadow bg-white';

const SectionCard = ({ icon: Icon, title, subtitle, children }) => (
  <section className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
    <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5" />
          </div>
        )}
        <div className="min-w-0">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const USERNAME_PATTERN = /^[a-z0-9][a-z0-9_-]{2,29}$/;
const USERNAME_CHECK_DEBOUNCE_MS = 600;

const MyWebsiteView = ({ showNotification }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [publicUsername, setPublicUsername] = useState('');
  const [savedPublicUsername, setSavedPublicUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, message: '' });
  const [isSavingUsername, setIsSavingUsername] = useState(false);
  const usernameCheckTimeoutRef = useRef(null);
  const usernameCheckAbortRef = useRef(null);
  const usernameCheckSeqRef = useRef(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingBusinessPhotos, setIsUploadingBusinessPhotos] = useState(false);
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const businessPhotosInputRef = useRef(null);
  const [websiteData, setWebsiteData] = useState({
    brandName: '',
    tagline: '',
    bio: '',
    location: '',
    address: '',
    since: '',
    website: '',
    contactEmail: '',
    avatarUrl: '',
    coverUrl: '',
    specialties: '',
    services: '',
    businessPhotos: '',
    whatsapp: '',
    instagram: '',
    facebook: '',
    linkedin: '',
    googleReviews: '',
    publicSiteTheme: 'indigo',
  });

  const digitsPhone = useMemo(() => phoneNumber.replace(/\D/g, ''), [phoneNumber]);
  const normalizedUsername = useMemo(() => publicUsername.trim().toLowerCase(), [publicUsername]);
  const publicPageUrl = useMemo(() => {
    const origin = getPublicSiteOrigin();
    if (normalizedUsername) return `${origin}/${normalizedUsername}`;
    if (digitsPhone) return `${origin}/${digitsPhone}`;
    return '';
  }, [normalizedUsername, digitsPhone]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsLoading(false);
          return;
        }
        const res = await getUserProfile(token);
        const userData = res.data.data.user;
        setPhoneNumber(userData.phone || '');
        setPublicUsername(userData.publicUsername || '');
        setSavedPublicUsername(userData.publicUsername || '');

        if (userData.publicProfile) {
          const profile = userData.publicProfile;
          const reviewsText = (profile.googleReviews || [])
            .map((review) => {
              const author = review.author || '';
              const rating = review.rating ?? '';
              const text = review.text || '';
              return `${author} | ${rating} | ${text}`.trim();
            })
            .filter(Boolean)
            .join('\n');
          setWebsiteData({
            brandName: profile.brandName || '',
            tagline: profile.tagline || '',
            bio: profile.bio || '',
            location: profile.location || '',
            address: profile.address || '',
            since: profile.since || '',
            website: profile.website || '',
            contactEmail: profile.contactEmail || '',
            avatarUrl: profile.avatarUrl || '',
            coverUrl: profile.coverUrl || '',
            specialties: Array.isArray(profile.specialties) ? profile.specialties.join(', ') : '',
            services: Array.isArray(profile.services) ? profile.services.join(', ') : '',
            businessPhotos: Array.isArray(profile.businessPhotos) ? profile.businessPhotos.join(', ') : '',
            whatsapp: profile.socials?.whatsapp || '',
            instagram: profile.socials?.instagram || '',
            facebook: profile.socials?.facebook || '',
            linkedin: profile.socials?.linkedin || '',
            googleReviews: reviewsText,
            publicSiteTheme: normalizePublicVendorThemeId(profile.publicSiteTheme),
          });
        }
      } catch (error) {
        console.error('Error loading user profile for website view:', error);
        showNotification('Failed to load your profile.', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [showNotification]);

  const validateUsernameFormat = useCallback((value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return { valid: true, message: '' };
    if (normalized.length < 3) return { valid: false, message: 'At least 3 characters.' };
    if (normalized.length > 30) return { valid: false, message: 'At most 30 characters.' };
    if (/^\d{10,}$/.test(normalized)) return { valid: false, message: 'Cannot be a phone number.' };
    if (!USERNAME_PATTERN.test(normalized)) {
      return {
        valid: false,
        message: 'Use letters, numbers, hyphens, or underscores. Must start with a letter or number.',
      };
    }
    return { valid: true, message: '' };
  }, []);

  useEffect(() => {
    if (usernameCheckTimeoutRef.current) {
      clearTimeout(usernameCheckTimeoutRef.current);
    }

    const normalized = normalizedUsername;
    if (!normalized) {
      if (usernameCheckAbortRef.current) {
        usernameCheckAbortRef.current.abort();
        usernameCheckAbortRef.current = null;
      }
      setUsernameStatus({ checking: false, available: null, message: '' });
      return undefined;
    }

    if (normalized === savedPublicUsername) {
      if (usernameCheckAbortRef.current) {
        usernameCheckAbortRef.current.abort();
        usernameCheckAbortRef.current = null;
      }
      setUsernameStatus({ checking: false, available: true, message: 'This is your current username.' });
      return undefined;
    }

    const formatCheck = validateUsernameFormat(normalized);
    if (!formatCheck.valid) {
      if (usernameCheckAbortRef.current) {
        usernameCheckAbortRef.current.abort();
        usernameCheckAbortRef.current = null;
      }
      setUsernameStatus({ checking: false, available: false, message: formatCheck.message });
      return undefined;
    }

    setUsernameStatus({ checking: true, available: null, message: 'Checking availability…' });
    usernameCheckTimeoutRef.current = setTimeout(async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setUsernameStatus({
          checking: false,
          available: null,
          message: 'Please log in again to check username availability.',
        });
        return;
      }

      if (usernameCheckAbortRef.current) {
        usernameCheckAbortRef.current.abort();
      }
      const controller = new AbortController();
      usernameCheckAbortRef.current = controller;
      const requestSeq = ++usernameCheckSeqRef.current;

      try {
        const res = await checkPublicUsernameAvailability(normalized, token, { signal: controller.signal });
        if (requestSeq !== usernameCheckSeqRef.current) return;

        const { available, message } = parseUsernameCheckPayload(res);
        setUsernameStatus({
          checking: false,
          available,
          message,
        });
      } catch (error) {
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
        if (requestSeq !== usernameCheckSeqRef.current) return;

        setUsernameStatus({
          checking: false,
          available: null,
          message: error.response?.data?.message || 'Could not check username. You can still try Save.',
        });
      } finally {
        if (usernameCheckAbortRef.current === controller) {
          usernameCheckAbortRef.current = null;
        }
      }
    }, USERNAME_CHECK_DEBOUNCE_MS);

    return () => {
      if (usernameCheckTimeoutRef.current) clearTimeout(usernameCheckTimeoutRef.current);
    };
  }, [normalizedUsername, savedPublicUsername, validateUsernameFormat]);

  const usernameHasChanges = normalizedUsername !== savedPublicUsername;

  const saveUsernameDisabledReason = useMemo(() => {
    if (!usernameHasChanges) return 'No changes to save.';
    if (usernameStatus.checking) return 'Checking username availability…';
    if (!normalizedUsername) return '';
    const formatCheck = validateUsernameFormat(normalizedUsername);
    if (!formatCheck.valid) return formatCheck.message;
    if (usernameStatus.available === false) {
      return usernameStatus.message || 'This username is not available.';
    }
    if (usernameStatus.available !== true) {
      return usernameStatus.message || 'Waiting for availability check…';
    }
    return '';
  }, [usernameHasChanges, usernameStatus, normalizedUsername, validateUsernameFormat]);

  const handleSaveUsername = async () => {
    if (!usernameHasChanges) return;

    const token = localStorage.getItem('token');
    if (!token) {
      showNotification('Please login to save your username.', 'error');
      return;
    }

    if (normalizedUsername) {
      const formatCheck = validateUsernameFormat(normalizedUsername);
      if (!formatCheck.valid) {
        showNotification(formatCheck.message, 'error');
        return;
      }

      if (normalizedUsername !== savedPublicUsername) {
        try {
          const res = await checkPublicUsernameAvailability(normalizedUsername, token);
          const { available, message } = parseUsernameCheckPayload(res);
          if (available !== true) {
            const msg = message || 'This username is already taken.';
            setUsernameStatus({ checking: false, available: available === false ? false : null, message: msg });
            showNotification(msg, 'error');
            return;
          }
        } catch (error) {
          const msg = error.response?.data?.message || 'Could not verify username availability.';
          setUsernameStatus({ checking: false, available: null, message: msg });
          showNotification(msg, 'error');
          return;
        }
      }
    }

    try {
      setIsSavingUsername(true);
      await updateUserProfile({ publicUsername: normalizedUsername || '' }, token);
      setSavedPublicUsername(normalizedUsername);
      setUsernameStatus({
        checking: false,
        available: true,
        message: normalizedUsername ? 'This is your current username.' : '',
      });
      showNotification(
        normalizedUsername ? 'Username saved. Your public link is ready to share.' : 'Username removed.',
        'success'
      );
    } catch (error) {
      console.error('Error saving username:', error);
      showNotification(error.response?.data?.message || 'Failed to save username.', 'error');
    } finally {
      setIsSavingUsername(false);
    }
  };

  const handleImageUpload = async (type, file) => {
    if (!file) return;

    const token = localStorage.getItem('token');
    if (!token) {
      showNotification('Please login to upload images.', 'error');
      return;
    }

    const isAvatar = type === 'avatar';
    try {
      if (isAvatar) {
        setIsUploadingAvatar(true);
      } else {
        setIsUploadingCover(true);
      }

      const formData = new FormData();
      formData.append('image', file);
      formData.append('folder', isAvatar ? 'public-profiles/avatars' : 'public-profiles/covers');

      const response = await fetch(`${API_BASE_URL}/s3/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Upload failed');
      }

      const data = await response.json();
      const imageUrl = data.url;

      if (isAvatar) {
        setWebsiteData((prev) => ({ ...prev, avatarUrl: imageUrl }));
      } else {
        setWebsiteData((prev) => ({ ...prev, coverUrl: imageUrl }));
      }

      showNotification('Image uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      showNotification(error.message || 'Failed to upload image.', 'error');
    } finally {
      if (isAvatar) {
        setIsUploadingAvatar(false);
      } else {
        setIsUploadingCover(false);
      }
    }
  };

  const handleBusinessPhotosUpload = async (files) => {
    if (!files || files.length === 0) return;

    const token = localStorage.getItem('token');
    if (!token) {
      showNotification('Please login to upload images.', 'error');
      return;
    }

    try {
      setIsUploadingBusinessPhotos(true);
      const uploadedUrls = [];

      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('image', files[i]);
        formData.append('folder', 'public-profiles/business-photos');

        const response = await fetch(`${API_BASE_URL}/s3/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Upload failed');
        }

        const data = await response.json();
        uploadedUrls.push(data.url);
      }

      const existingPhotos = websiteData.businessPhotos
        ? websiteData.businessPhotos.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
      const allPhotos = [...existingPhotos, ...uploadedUrls];
      setWebsiteData((prev) => ({ ...prev, businessPhotos: allPhotos.join(', ') }));

      showNotification(`${uploadedUrls.length} photo(s) uploaded successfully!`, 'success');
    } catch (error) {
      console.error('Error uploading business photos:', error);
      showNotification(error.message || 'Failed to upload business photos.', 'error');
    } finally {
      setIsUploadingBusinessPhotos(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Please login to save your website.', 'error');
        return;
      }

      const themeId = normalizePublicVendorThemeId(websiteData.publicSiteTheme);

      const parseReviews = (text) =>
        String(text || '')
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const [author, rating, ...rest] = line.split('|').map((part) => part.trim());
            return {
              author: author || 'Customer',
              rating: Number(rating) || 5,
              text: rest.join(' | ').trim() || '',
            };
          });

      const payload = {
        publicProfile: {
          brandName: websiteData.brandName,
          tagline: websiteData.tagline,
          bio: websiteData.bio,
          location: websiteData.location,
          address: websiteData.address,
          since: websiteData.since,
          website: websiteData.website,
          contactEmail: websiteData.contactEmail,
          avatarUrl: websiteData.avatarUrl,
          coverUrl: websiteData.coverUrl,
          specialties: websiteData.specialties.split(',').map((s) => s.trim()).filter(Boolean),
          services: websiteData.services.split(',').map((s) => s.trim()).filter(Boolean),
          businessPhotos: websiteData.businessPhotos.split(',').map((s) => s.trim()).filter(Boolean),
          googleReviews: parseReviews(websiteData.googleReviews),
          socials: {
            instagram: websiteData.instagram,
            facebook: websiteData.facebook,
            linkedin: websiteData.linkedin,
            whatsapp: websiteData.whatsapp,
          },
          publicSiteTheme: themeId,
        },
      };

      await updateUserProfile(payload, token);
      setWebsiteData((prev) => ({ ...prev, publicSiteTheme: themeId }));
      showNotification('Website details and public theme saved.', 'success');
    } catch (error) {
      console.error('Error saving website:', error);
      showNotification(error.response?.data?.message || 'Failed to save website details.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 size={40} className="text-violet-600 animate-spin" />
        <p className="text-sm text-slate-500">Loading your public page settings…</p>
      </div>
    );
  }

  if (!phoneNumber) {
    return (
      <div className="max-w-lg mx-auto mt-8 rounded-2xl border border-amber-200 bg-amber-50/80 p-8 text-center">
        <Globe className="w-12 h-12 text-amber-600 mx-auto mb-3" />
        <p className="text-slate-800 font-semibold">Phone number required</p>
        <p className="text-slate-600 text-sm mt-2">Add a phone number to your account to publish a vendor page.</p>
      </div>
    );
  }

  const selectedThemeMeta = VENDOR_THEME_OPTIONS.find((t) => t.id === websiteData.publicSiteTheme) || VENDOR_THEME_OPTIONS[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-violet-50/30 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div>
            <p className="text-sm font-medium text-violet-600 mb-1">My Website</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Public fabric vendor page</h1>
            <p className="text-slate-600 mt-2 max-w-2xl text-sm sm:text-base">
              Shape how buyers see your brand: choose a colour theme for your public URL, then keep profile and media up to
              date.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-sm text-slate-700 shadow-sm">
              <span className="text-slate-400">ID</span>
              <span className="font-mono font-semibold">{digitsPhone || '—'}</span>
            </span>
          </div>
        </header>

        <div className="grid xl:grid-cols-3 gap-8 items-start">
          <div className="xl:col-span-2 space-y-6">
            <SectionCard
              icon={AtSign}
              title="Public username"
              subtitle="Choose a unique handle for your vendor page URL."
            >
              <label className="block text-sm font-medium text-slate-700 mb-2">Username</label>
              <div className="flex rounded-xl border border-slate-200 overflow-hidden bg-white focus-within:ring-2 focus-within:ring-violet-500/30 focus-within:border-violet-500">
                <span className="px-3 py-2.5 text-sm text-slate-500 bg-slate-50 border-r border-slate-200 shrink-0">
                  {getPublicSiteOrigin().replace(/^https?:\/\//, '')}/
                </span>
                <input
                  type="text"
                  value={publicUsername}
                  onChange={(e) => setPublicUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                  placeholder="your-brand"
                  className="flex-1 px-3 py-2.5 text-sm outline-none"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              {usernameStatus.message ? (
                <p
                  className={`mt-2 text-xs ${
                    usernameStatus.checking
                      ? 'text-slate-500'
                      : usernameStatus.available === true
                        ? 'text-emerald-600'
                        : usernameStatus.available === false
                          ? 'text-rose-600'
                          : 'text-amber-600'
                  }`}
                >
                  {usernameStatus.message}
                </p>
              ) : saveUsernameDisabledReason ? (
                <p className="mt-2 text-xs text-amber-600">{saveUsernameDisabledReason}</p>
              ) : null}
              <p className="mt-2 text-xs text-slate-500">
                3–30 characters. Letters, numbers, hyphens, and underscores only. Your phone-number link still works if you already shared it.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleSaveUsername}
                  disabled={
                    isSavingUsername ||
                    !usernameHasChanges ||
                    usernameStatus.checking ||
                    Boolean(normalizedUsername && usernameStatus.available === false)
                  }
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {isSavingUsername ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Save username
                </button>
                {saveUsernameDisabledReason && !isSavingUsername && (
                  <span className="text-xs text-slate-500">{saveUsernameDisabledReason}</span>
                )}
              </div>
            </SectionCard>

            <SectionCard icon={Palette} title="Public page theme" subtitle="Applies to your customer-facing fabric page (colours, hero, tabs, collection cards).">
              <p className="text-sm text-slate-600 mb-4">
                Selected: <span className="font-semibold text-slate-900">{selectedThemeMeta.name}</span> —{' '}
                {selectedThemeMeta.description}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {VENDOR_THEME_OPTIONS.map((opt) => {
                  const vars = getVendorThemeCssVars(opt.id);
                  const selected = websiteData.publicSiteTheme === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setWebsiteData((prev) => ({ ...prev, publicSiteTheme: opt.id }))}
                      className={`relative text-left rounded-2xl border-2 p-3 transition-all overflow-hidden ${
                        selected ? 'border-violet-600 ring-2 ring-violet-200 shadow-md' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div
                        className="h-14 rounded-xl mb-2 flex overflow-hidden ring-1 ring-black/5"
                        style={{
                          ...vars,
                          background: `linear-gradient(135deg, var(--pv-hero-from), var(--pv-hero-via) 50%, var(--pv-page-to))`,
                        }}
                      />
                      <div className="font-bold text-slate-900 text-sm">{opt.name}</div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{opt.description}</p>
                      {selected && (
                        <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-violet-600 text-white flex items-center justify-center shadow">
                          <Check className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </SectionCard>

            <SectionCard icon={Store} title="Brand story" subtitle="Headline and introduction on your public page.">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Brand name *</label>
                  <input
                    type="text"
                    value={websiteData.brandName}
                    onChange={(e) => setWebsiteData({ ...websiteData, brandName: e.target.value })}
                    placeholder="e.g., Elegant Fabrics"
                    className={inputClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Tagline</label>
                  <input
                    type="text"
                    value={websiteData.tagline}
                    onChange={(e) => setWebsiteData({ ...websiteData, tagline: e.target.value })}
                    placeholder="Premium textiles since 1990"
                    className={inputClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">About Us</label>
                  <textarea
                    value={websiteData.bio}
                    onChange={(e) => setWebsiteData({ ...websiteData, bio: e.target.value })}
                    placeholder="What makes your fabrics special?"
                    rows={4}
                    className={inputClass}
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard icon={MapPin} title="Contact & location" subtitle="Shown on your public profile card.">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Location</label>
                  <input
                    type="text"
                    value={websiteData.location}
                    onChange={(e) => setWebsiteData({ ...websiteData, location: e.target.value })}
                    placeholder="City, Country"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Since</label>
                  <input
                    type="text"
                    value={websiteData.since}
                    onChange={(e) => setWebsiteData({ ...websiteData, since: e.target.value })}
                    placeholder="Year"
                    className={inputClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Address</label>
                  <input
                    type="text"
                    value={websiteData.address}
                    onChange={(e) => setWebsiteData({ ...websiteData, address: e.target.value })}
                    placeholder="Full address"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Email</label>
                  <input
                    type="email"
                    value={websiteData.contactEmail}
                    onChange={(e) => setWebsiteData({ ...websiteData, contactEmail: e.target.value })}
                    placeholder="contact@yourbrand.com"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Website</label>
                  <input
                    type="url"
                    value={websiteData.website}
                    onChange={(e) => setWebsiteData({ ...websiteData, website: e.target.value })}
                    placeholder="https://"
                    className={inputClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">WhatsApp</label>
                  <input
                    type="text"
                    value={websiteData.whatsapp}
                    onChange={(e) => setWebsiteData({ ...websiteData, whatsapp: e.target.value })}
                    placeholder="https://wa.me/919876543210"
                    className={inputClass}
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard icon={MessageCircle} title="Reviews" subtitle="Customer reviews shown on your public page. One per line: Name | Rating | Review">
              <textarea
                value={websiteData.googleReviews}
                onChange={(e) => setWebsiteData({ ...websiteData, googleReviews: e.target.value })}
                placeholder={"Priya Sharma | 5 | Excellent quality fabrics and fast delivery.\nRahul Mehta | 4.5 | Great wholesale pricing."}
                rows={5}
                className={inputClass}
              />
            </SectionCard>

            <SectionCard icon={Sparkles} title="Specialties & services" subtitle="Comma-separated lists.">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Specialties</label>
                  <input
                    type="text"
                    value={websiteData.specialties}
                    onChange={(e) => setWebsiteData({ ...websiteData, specialties: e.target.value })}
                    placeholder="Silk, Cotton, Linen"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Services</label>
                  <input
                    type="text"
                    value={websiteData.services}
                    onChange={(e) => setWebsiteData({ ...websiteData, services: e.target.value })}
                    placeholder="Wholesale, Sampling"
                    className={inputClass}
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard icon={Camera} title="Media" subtitle="Logo, cover, and gallery images.">
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Logo / avatar</label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="url"
                      value={websiteData.avatarUrl}
                      onChange={(e) => setWebsiteData({ ...websiteData, avatarUrl: e.target.value })}
                      placeholder="Image URL"
                      className={`${inputClass} flex-1 text-sm`}
                    />
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={isUploadingAvatar}
                      className="px-4 py-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-semibold shrink-0"
                    >
                      {isUploadingAvatar ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                      Upload
                    </button>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload('avatar', e.target.files?.[0])}
                    />
                  </div>
                  {websiteData.avatarUrl && (
                    <img src={websiteData.avatarUrl} alt="" className="mt-2 h-20 w-20 object-cover rounded-xl border border-slate-200" />
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Cover image</label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="url"
                      value={websiteData.coverUrl}
                      onChange={(e) => setWebsiteData({ ...websiteData, coverUrl: e.target.value })}
                      placeholder="Image URL"
                      className={`${inputClass} flex-1 text-sm`}
                    />
                    <button
                      type="button"
                      onClick={() => coverInputRef.current?.click()}
                      disabled={isUploadingCover}
                      className="px-4 py-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-semibold shrink-0"
                    >
                      {isUploadingCover ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                      Upload
                    </button>
                    <input
                      ref={coverInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload('cover', e.target.files?.[0])}
                    />
                  </div>
                  {websiteData.coverUrl && (
                    <img src={websiteData.coverUrl} alt="" className="mt-2 h-28 w-full max-w-md object-cover rounded-xl border border-slate-200" />
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Business photos</label>
                  <div className="flex flex-col sm:flex-row gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => businessPhotosInputRef.current?.click()}
                      disabled={isUploadingBusinessPhotos}
                      className="px-4 py-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition disabled:opacity-50 inline-flex items-center justify-center gap-2 text-sm font-semibold w-fit"
                    >
                      {isUploadingBusinessPhotos ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                      Upload multiple
                    </button>
                    <input
                      ref={businessPhotosInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => handleBusinessPhotosUpload(e.target.files)}
                    />
                  </div>
                  <input
                    type="text"
                    value={websiteData.businessPhotos}
                    onChange={(e) => setWebsiteData({ ...websiteData, businessPhotos: e.target.value })}
                    placeholder="Or comma-separated URLs"
                    className={`${inputClass} text-sm`}
                  />
                </div>
              </div>
            </SectionCard>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !websiteData.brandName}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-slate-900/10"
              >
                {isSaving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
                Save all changes
              </button>
            </div>
          </div>

          <aside className="xl:col-span-1 space-y-4 xl:sticky xl:top-6">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-lg overflow-hidden">
              <div className="bg-gradient-to-br from-violet-600 via-fuchsia-600 to-indigo-600 px-5 py-4 text-white">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Globe className="w-5 h-5 opacity-90" />
                  Your public link
                </h3>
                <p className="text-sm text-white/85 mt-1">Share this URL with buyers — theme updates apply instantly after save.</p>
              </div>
              <div className="p-5 space-y-4">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Vendor page</p>
                  <p className="text-sm font-mono text-violet-700 break-all">{publicPageUrl || 'Set a username or phone to get your link'}</p>
                  {normalizedUsername && digitsPhone && (
                    <p className="text-xs text-slate-500 mt-2 break-all">
                      Phone link: {getPublicSiteOrigin()}/{digitsPhone}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <a
                    href={publicPageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-violet-600 text-white font-semibold hover:bg-violet-700 transition text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open public page
                  </a>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(publicPageUrl);
                        showNotification('Link copied', 'success');
                      } catch {
                        showNotification('Could not copy', 'error');
                      }
                    }}
                    className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-xl border-2 border-slate-200 text-slate-800 font-semibold hover:bg-slate-50 text-sm"
                  >
                    <Copy className="w-4 h-4" />
                    Copy link
                  </button>
                </div>
                <div className="border-t border-slate-100 pt-4 space-y-2">
                  <button
                    type="button"
                    onClick={() => {
                      const message = `${websiteData.brandName || 'My fabrics'}\n${websiteData.tagline || ''}\n\n${publicPageUrl}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                      showNotification('Opening WhatsApp…', 'success');
                    }}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-emerald-500 text-emerald-700 font-semibold hover:bg-emerald-50 text-sm"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Share on WhatsApp
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      const followUrl = `${getPublicSiteOrigin()}/follow/${digitsPhone}`;
                      try {
                        await navigator.clipboard.writeText(followUrl);
                        showNotification('Follow link copied', 'success');
                      } catch {
                        showNotification('Could not copy', 'error');
                      }
                    }}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold hover:from-pink-600 hover:to-rose-600 text-sm"
                  >
                    <Bell className="w-4 h-4" />
                    Copy follow link
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default MyWebsiteView;
